<template>
  <div class="withdraw-page">
    <header class="page-header">
      <button class="back-button" type="button" aria-label="返回" @click="$router.back()">
        <span>‹</span>
      </button>
      <h1>提现</h1>
    </header>

    <main class="page-content">
      <section class="balance-card">
        <div>
          <p class="balance-label">主钱包余额（元）</p>
          <p class="balance-value">{{ displayBalance }}</p>
        </div>
        <button class="recover-button" type="button" :disabled="recovering" @click="transall">
          {{ recovering ? '回收中...' : '一键回收' }}
        </button>
      </section>

      <section v-if="!hasBinding" class="empty-card">
        <div class="empty-title">还没有绑定提现账号</div>
        <p class="empty-text">先去实名认证页完成实名并绑定银行卡、支付宝、USDT。</p>
        <button class="empty-btn" type="button" @click="$router.push('/addBankCard')">去绑定</button>
      </section>

      <section class="form-card">
        <div class="section-title">选择提现账号</div>
        <div class="method-tabs" role="tablist" aria-label="提现账号">
          <button
            v-for="item in methods"
            :key="item.key"
            type="button"
            class="method-tab"
            :class="{ active: payoutMethod === item.key }"
            :disabled="!bindings[item.key]"
            @click="payoutMethod = item.key"
          >
            <span class="method-icon" :class="item.key">{{ item.label }}</span>
            <span class="method-name">{{ item.name }}</span>
          </button>
        </div>

        <div class="binding-panel" v-if="currentBinding">
          <div class="binding-line">姓名：{{ currentBinding.account_name }}</div>
          <div class="binding-line">
            账号：
            <span v-if="payoutMethod === 'usdt'">{{ currentBinding.account_no }} / {{ currentBinding.bank_name || 'TRC20' }}</span>
            <span v-else>{{ currentBinding.account_no }}</span>
          </div>
          <div class="binding-note">绑定后不可随意更换，如需更换请联系客服。</div>
        </div>

        <div v-else class="binding-panel binding-panel--empty">
          该类型暂未绑定，请先绑定后再提现。
        </div>
      </section>

      <section class="form-card amount-card">
        <h2>提现金额</h2>
        <div class="amount-input-wrap">
          <span class="currency-symbol">¥</span>
          <input
            v-model.trim="amount"
            type="number"
            min="0"
            step="0.01"
            inputmode="decimal"
            placeholder="请输入提现金额"
          />
          <button type="button" @click="useMaxAmount">全部</button>
        </div>
        <p class="amount-limit">单笔限额：{{ formatAmount(minAmount) }} - {{ formatAmount(maxAmount) }} 元</p>
        <div
          v-if="turnover.multiple > 0 && turnover.recharge_amount > 0"
          class="turnover-status"
          :class="{ passed: turnover.passed }"
        >
          <div class="turnover-title">
            <span>有效投注要求（{{ formatAmount(turnover.multiple) }}倍）</span>
            <strong>{{ turnover.passed ? '已达到' : '未达到' }}</strong>
          </div>
          <div class="turnover-progress">
            <i :style="{ width: turnoverProgress + '%' }"></i>
          </div>
          <p>
            已完成 {{ formatAmount(turnover.bet_amount) }} / 要求 {{ formatAmount(turnover.required_amount) }}
            <span v-if="!turnover.passed">，还差 {{ formatAmount(turnover.remaining_amount) }}</span>
          </p>
        </div>
        <div v-if="quickAmounts.length" class="quick-amounts">
          <button
            v-for="item in quickAmounts"
            :key="item"
            type="button"
            :class="{ active: Number(amount) === Number(item) }"
            @click="amount = String(item)"
          >
            {{ formatAmount(item) }}
          </button>
        </div>

        <label class="field-block password-field">
          <span class="field-label">提现密码</span>
          <input
            v-model="password"
            type="password"
            autocomplete="new-password"
            placeholder="请输入提现密码"
          />
        </label>
      </section>

      <button class="submit-button" type="button" :disabled="submitting || !currentBinding" @click="submitWithdrawal">
        {{ submitting ? '提交中...' : '确认提现' }}
      </button>
      <p class="submit-note">提交后请等待后台审核，审核结果可在交易记录中查看。</p>
    </main>
  </div>
</template>

<script>
export default {
  name: 'Withdrawal',
  data() {
    return {
      payoutMethod: 'bank',
      methods: [
        { key: 'bank', name: '银行卡', label: '银' },
        { key: 'alipay', name: '支付宝', label: '支' },
        { key: 'usdt', name: 'USDT', label: 'U' }
      ],
      bindings: {
        bank: null,
        alipay: null,
        usdt: null
      },
      amount: '',
      password: '',
      minAmount: 100,
      maxAmount: 500000,
      quickAmounts: [100, 300, 500, 1000, 3000, 5000, 10000],
      turnover: {
        multiple: 0,
        recharge_amount: 0,
        bet_amount: 0,
        required_amount: 0,
        remaining_amount: 0,
        passed: true
      },
      submitting: false,
      recovering: false
    };
  },
  computed: {
    balance() {
      const userInfo = (this.$store && this.$store.state && this.$store.state.userInfo) || {};
      const value = Number(userInfo.balance);
      return Number.isFinite(value) ? value : 0;
    },
    displayBalance() {
      return this.balance.toFixed(2);
    },
    currentBinding() {
      return this.bindings[this.payoutMethod] || null;
    },
    hasBinding() {
      return !!(this.bindings.bank || this.bindings.alipay || this.bindings.usdt);
    },
    turnoverProgress() {
      if (this.turnover.required_amount <= 0) {
        return 100;
      }
      return Math.min(100, Math.round((this.turnover.bet_amount / this.turnover.required_amount) * 100));
    }
  },
  created() {
    this.loadWithdrawConfig();
    this.loadBindings();
  },
  methods: {
    notify(type, message) {
      if (this.$parent && typeof this.$parent.showTost === 'function') {
        this.$parent.showTost(type, message);
      }
    },
    showLoading() {
      if (this.$parent && typeof this.$parent.showLoading === 'function') {
        this.$parent.showLoading();
      }
    },
    hideLoading() {
      if (this.$parent && typeof this.$parent.hideLoading === 'function') {
        this.$parent.hideLoading();
      }
    },
    async loadWithdrawConfig() {
      try {
        const res = await this.$apiFun.get('/api/withdraw/config');
        if (Number(res.code) !== 200 || !res.data) {
          return;
        }

        const min = Number(res.data.min_amount);
        const max = Number(res.data.max_amount);
        if (Number.isFinite(min) && min > 0) {
          this.minAmount = min;
        }
        if (Number.isFinite(max) && max >= this.minAmount) {
          this.maxAmount = max;
        }

        if (Array.isArray(res.data.quick_amounts)) {
          const amounts = res.data.quick_amounts
            .map(item => Number(item))
            .filter(item => Number.isFinite(item) && item >= this.minAmount && item <= this.maxAmount);
          this.quickAmounts = Array.from(new Set(amounts));
        }

        this.updateTurnover(res.data.turnover);
      } catch (error) {
        // 使用默认配置兜底。
      }
    },
    async loadBindings() {
      try {
        const res = await this.$apiFun.post('/api/realname-status', {});
        if (Number(res.code) !== 200 || !res.data) {
          return;
        }
        const data = res.data;
        this.bindings = {
          bank: data.bindings && data.bindings.bank ? data.bindings.bank : null,
          alipay: data.bindings && data.bindings.alipay ? data.bindings.alipay : null,
          usdt: data.bindings && data.bindings.usdt ? data.bindings.usdt : null
        };
        if (!this.bindings[this.payoutMethod]) {
          const first = ['bank', 'alipay', 'usdt'].find(key => this.bindings[key]);
          if (first) {
            this.payoutMethod = first;
          }
        }
      } catch (error) {
        // 忽略，页面会显示空状态。
      }
    },
    formatAmount(value) {
      const number = Number(value);
      if (!Number.isFinite(number)) {
        return '0';
      }
      return number % 1 === 0 ? String(number) : number.toFixed(2);
    },
    updateTurnover(data) {
      if (!data || typeof data !== 'object') {
        return;
      }
      const numericFields = ['multiple', 'recharge_amount', 'bet_amount', 'required_amount', 'remaining_amount'];
      numericFields.forEach(field => {
        const value = Number(data[field]);
        this.turnover[field] = Number.isFinite(value) ? value : 0;
      });
      this.turnover.passed = Boolean(data.passed);
    },
    async transall() {
      if (this.recovering) {
        return;
      }
      this.recovering = true;
      this.showLoading();
      try {
        const res = await this.$apiFun.post('/api/transall', {});
        this.notify(Number(res.code) === 200 ? 1 : 0, res.message || '回收完成');
        if (Number(res.code) === 200 && this.$parent && typeof this.$parent.getUserInfo === 'function') {
          await this.$parent.getUserInfo();
        }
      } catch (error) {
        this.notify(0, '网络异常，请稍后重试');
      } finally {
        this.recovering = false;
        this.hideLoading();
      }
    },
    validateForm() {
      const amount = Number(this.amount);

      if (!this.currentBinding) {
        return '请先绑定提现账号';
      }
      if (!Number.isFinite(amount) || amount <= 0) {
        return '请输入正确的提现金额';
      }
      if (amount < this.minAmount) {
        return `单次提现最低金额为 ${this.formatAmount(this.minAmount)} 元`;
      }
      if (amount > this.maxAmount) {
        return `单次提现最高金额为 ${this.formatAmount(this.maxAmount)} 元`;
      }
      if (amount > this.balance) {
        return '账户余额不足';
      }
      if (!this.password) {
        return '请输入提现密码';
      }

      return '';
    },
    useMaxAmount() {
      this.amount = this.formatAmount(this.balance);
    },
    async submitWithdrawal() {
      if (this.submitting) {
        return;
      }
      const errorMessage = this.validateForm();
      if (errorMessage) {
        this.notify(0, errorMessage);
        return;
      }

      const formData = new FormData();
      formData.append('binding_type', this.payoutMethod);
      formData.append('amount', Number(this.amount).toFixed(2));
      formData.append('password', this.password);

      this.submitting = true;
      this.showLoading();
      try {
        const res = await this.$apiFun.post('/api/withdraw', formData);
        if (Number(res.code) !== 200) {
          if (Number(res.code) === 214) {
            this.updateTurnover(res.data);
          }
          this.notify(0, res.message || '提现申请提交失败');
          return;
        }

        this.notify(1, res.message || '提现申请已提交，等待审核');
        if (this.$parent && typeof this.$parent.getUserInfo === 'function') {
          this.$parent.getUserInfo();
        }
        this.amount = '';
        this.password = '';
        setTimeout(() => {
          this.$router.push({ path: '/transRecord' });
        }, 800);
      } catch (error) {
        this.notify(0, '网络异常，请稍后重试');
      } finally {
        this.submitting = false;
        this.hideLoading();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.withdraw-page {
  min-height: 100vh;
  background: #f4f6fa;
  color: #1d2635;
  padding-bottom: calc(32px + env(safe-area-inset-bottom));
}

.page-header {
  position: sticky;
  top: 0;
  z-index: 20;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(135deg, #1768e5, #0846b7);

  h1 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
}

.back-button {
  position: absolute;
  left: 8px;
  top: 0;
  width: 44px;
  height: 48px;
  border: 0;
  color: #fff;
  background: transparent;
  font-size: 34px;
  line-height: 44px;
}

.page-content {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 14px 12px 0;
  box-sizing: border-box;
}

.balance-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 92px;
  padding: 18px;
  box-sizing: border-box;
  border-radius: 14px;
  color: #fff;
  background: linear-gradient(135deg, #2477ed, #1254c5);
  box-shadow: 0 8px 18px rgba(18, 84, 197, 0.2);
}

.balance-label {
  margin: 0 0 7px;
  font-size: 13px;
  opacity: 0.82;
}

.balance-value {
  margin: 0;
  font-size: 27px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.recover-button {
  padding: 8px 15px;
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 18px;
  color: #fff;
  background: rgba(255, 255, 255, 0.12);
  font-size: 13px;

  &:disabled {
    opacity: 0.6;
  }
}

.form-card,
.empty-card {
  margin-top: 12px;
  padding: 17px 15px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(25, 52, 91, 0.05);
}

.empty-title {
  font-size: 16px;
  font-weight: 650;
}

.empty-text {
  margin: 8px 0 0;
  font-size: 13px;
  color: #7b8498;
  line-height: 1.5;
}

.empty-btn {
  margin-top: 12px;
  width: 100%;
  height: 42px;
  border: 0;
  border-radius: 21px;
  color: #fff;
  background: linear-gradient(135deg, #2f6df6, #084ccb);
}

.section-title {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 650;
}

.method-tabs {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.method-tab {
  min-height: 58px;
  border: 0;
  border-radius: 10px;
  padding: 10px 8px;
  background: #f6f8fc;
  color: #364257;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.method-tab:disabled {
  opacity: 0.45;
}

.method-tab.active {
  background: #084ccb;
  color: #fff;
}

.method-icon {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  line-height: 26px;
  text-align: center;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.18);
}

.method-name {
  font-size: 13px;
}

.binding-panel {
  margin-top: 12px;
  padding: 12px;
  border-radius: 10px;
  background: #f7f9fd;
}

.binding-panel--empty {
  color: #7b8498;
  font-size: 13px;
}

.binding-line {
  font-size: 13px;
  line-height: 1.8;
  color: #334155;
}

.binding-note {
  margin-top: 6px;
  font-size: 12px;
  color: #7b8498;
}

.amount-card {
  margin-top: 12px;
  padding: 17px 15px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(25, 52, 91, 0.05);

  h2 {
    margin: 0 0 14px;
    font-size: 16px;
    font-weight: 650;
  }
}

.amount-input-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 46px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f7f9fd;

  .currency-symbol {
    font-size: 18px;
    color: #2f6df6;
    font-weight: 700;
  }

  input {
    flex: 1;
    border: 0;
    outline: none;
    background: transparent;
    font-size: 15px;
  }

  button {
    width: 50px;
    height: 30px;
    border: 0;
    border-radius: 8px;
    color: #fff;
    background: #2f6df6;
    font-size: 12px;
  }
}

.amount-limit {
  margin: 10px 0 0;
  font-size: 12px;
  color: #7b8498;
}

.turnover-status {
  margin-top: 12px;
  padding: 12px;
  border-radius: 10px;
  background: #f7f9fd;
}

.turnover-status.passed {
  background: #eef9f0;
}

.turnover-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
}

.turnover-progress {
  height: 6px;
  border-radius: 999px;
  overflow: hidden;
  background: #dde4f0;
  margin: 8px 0;

  i {
    display: block;
    height: 100%;
    background: #2f6df6;
  }
}

.quick-amounts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;

  button {
    min-width: 62px;
    height: 32px;
    border: 0;
    border-radius: 16px;
    padding: 0 10px;
    background: #eef3ff;
    color: #2852d8;
  }

  button.active {
    color: #fff;
    background: #2852d8;
  }
}

.field-block {
  display: flex;
  flex-direction: column;
  margin-top: 12px;
}

.field-label {
  margin-bottom: 8px;
  font-size: 14px;
  color: #4b5568;
}

.password-field input {
  height: 44px;
  padding: 0 12px;
  border: 0;
  border-radius: 10px;
  background: #f7f9fd;
  outline: none;
}

.submit-button {
  width: 100%;
  height: 46px;
  margin-top: 12px;
  border: 0;
  border-radius: 23px;
  color: #fff;
  background: linear-gradient(135deg, #2f6df6, #084ccb);
  font-size: 16px;
}

.submit-button:disabled {
  opacity: 0.6;
}

.submit-note {
  margin: 10px 0 0;
  color: #7b8498;
  font-size: 12px;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/components/withdrawal.vue